import React from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Checkbox } from '@attentive-platform/stem-ui';
import { ArrowDownIcon, ArrowUpIcon } from '../rosettaSvgs';
import { SCOPE_TYPE_ENUM } from '../../../Constants/Constants';
import { useRosettaStore } from '../../../Stores/useRosettaStore';
import { ScopeCategory, CustomRequirement } from './TradeScopeComponents';

const TradesPanel = () => {
  const { tradesData, selectedTrades, expandedTrades, selectedScopes, customScopes, dispatch } = useRosettaStore(
    useShallow(state => ({
      tradesData: state.tradesData,
      selectedTrades: state.selectedTrades,
      expandedTrades: state.expandedTrades,
      selectedScopes: state.selectedScopes,
      customScopes: state.customScopes,
      dispatch: state.dispatch
    }))
  );

  const toggleTradeSelection = tradeId => {
    dispatch({ type: 'TOGGLE_TRADE_SELECTION', payload: tradeId });
  };

  const toggleTradeDropdown = tradeId => {
    dispatch({ type: 'TOGGLE_TRADE_DROPDOWN', payload: tradeId });
  };

  const toggleScopeSelection = (tradeId, scopeId) => {
    const trade = tradesData.find(t => t.trade_id === tradeId);
    const scope = trade?.scopes.find(s => s.scope_id === scopeId);

    // Don't allow toggling of default selected scopes
    if (scope?.is_default_selected) {
      return;
    }

    dispatch({ type: 'TOGGLE_SCOPE_SELECTION', payload: { tradeId, scopeId } });
  };

  const handleScopeItemClick = scope => {
    dispatch({ type: 'SET_SCOPE_ITEM_CLICK', payload: { scope } });
  };

  const updateCustomScope = (tradeId, value) => {
    dispatch({ type: 'UPDATE_CUSTOM_SCOPE', payload: { tradeId, value } });
  };

  const handleCustomScopeChange = (tradeId, e) => {
    updateCustomScope(tradeId, e.target.value);
  };

  return (
    <aside className='rosetta-panel'>
      <div className='rosetta-panel-header'>
        <h2>Trades & Scope</h2>
      </div>
      <div className='rosetta-panel-content'>
        {tradesData.map(trade => (
          <div key={trade.trade_id} className='rosetta-trade-item'>
            <div className='rosetta-trade-header' onClick={() => toggleTradeDropdown(trade.trade_id)}>
              <Checkbox
                checked={selectedTrades.has(trade.trade_id)}
                onChange={() => toggleTradeSelection(trade.trade_id)}
                size='small'
              />
              <div className='rosetta-trade-name'>{trade.trade_name}</div>
              <div className='rosetta-trade-arrow'>
                {expandedTrades.has(trade.trade_id) ? <ArrowDownIcon /> : <ArrowUpIcon />}
              </div>
            </div>

            {expandedTrades.has(trade.trade_id) && (
              <div className='rosetta-scope-items'>
                <ScopeCategory
                  title='BASIC SCOPE'
                  scopes={trade.scopes.filter(scope => scope.scope_type === SCOPE_TYPE_ENUM.BASIC)}
                  tradeId={trade.trade_id}
                  selectedScopes={selectedScopes[trade.trade_id]}
                  onScopeItemClick={handleScopeItemClick}
                  onToggleScope={toggleScopeSelection}
                />

                <ScopeCategory
                  title='ADVANCED SCOPE'
                  scopes={trade.scopes.filter(scope => scope.scope_type === SCOPE_TYPE_ENUM.ADVANCED)}
                  tradeId={trade.trade_id}
                  selectedScopes={selectedScopes[trade.trade_id]}
                  onScopeItemClick={handleScopeItemClick}
                  onToggleScope={toggleScopeSelection}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Company-Specific Requirements Section */}
      <div className='rosetta-custom-scope-section'>
        <h3>Company Specific Requirements</h3>
        <div className='rosetta-custom-scope-container'>
          {selectedTrades.size === 0 ? (
            <p className='rosetta-custom-scope-placeholder'>Select trades to add custom requirements</p>
          ) : (
            Array.from(selectedTrades).map(tradeId => {
              const trade = tradesData.find(t => t.trade_id === tradeId);
              if (!trade) return null;

              return (
                <CustomRequirement
                  key={tradeId}
                  trade={trade}
                  customScope={customScopes[tradeId] || ''}
                  onChange={e => handleCustomScopeChange(tradeId, e)}
                />
              );
            })
          )}
        </div>
      </div>
    </aside>
  );
};

export default TradesPanel;
